import { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { NavigationStep } from '../routes/CardCustomizer/CardCustomizer.types';
import { useCallback, useMemo } from 'react';
import { isPostcardCardSize } from '../utils/cardSizeName';

interface CardCustomizerConfig {
  steps: NavigationStep[];
}

type CardDimensions = Readonly<{ width: number; height: number }>;

const CONFIG_BASE: Record<StationeryDraftFormat, CardCustomizerConfig> = {
  [StationeryDraftFormat.digital]: {
    steps: ['cardFront', 'cardBack', 'envelope', 'review', 'postCheckout', 'success'],
  },
  [StationeryDraftFormat.print]: {
    steps: ['cardFront', 'cardBack', 'envelope', 'recipients', 'review', 'postCheckout'],
  },
};

const OVERRIDES: Record<StationeryDraftFormat, Partial<Record<StationeryTemplateCategoryEnum, CardCustomizerConfig>>> = {
  [StationeryDraftFormat.digital]: {},
  [StationeryDraftFormat.print]: {
    [StationeryTemplateCategoryEnum.enclosure]: {
      steps: ['cardFront', 'cardBack', 'review', 'postCheckout'],
    },
    [StationeryTemplateCategoryEnum.menu]: {
      steps: ['cardFront', 'review', 'postCheckout'],
    },
    [StationeryTemplateCategoryEnum.napkin]: {
      steps: ['cardFront', 'review', 'postCheckout'],
    },
    [StationeryTemplateCategoryEnum.sign]: {
      steps: ['cardFront', 'review', 'postCheckout'],
    },
  },
};

// Postcards have no envelopes — the card back is the hand-addressed mailing
// surface — so the envelope and recipient-addressing steps don't apply.
const POSTCARD_EXCLUDED_STEPS: ReadonlyArray<NavigationStep> = ['envelope', 'recipients'];

export const resolveCardCustomizerConfig = (format: StationeryDraftFormat, category: StationeryTemplateCategoryEnum, dimensions?: CardDimensions): CardCustomizerConfig => {
  const config = { ...CONFIG_BASE[format], ...(OVERRIDES[format]?.[category] || {}) };
  const isPostcard =
    format === StationeryDraftFormat.print && category === StationeryTemplateCategoryEnum.thankYou && !!dimensions && isPostcardCardSize(dimensions.width, dimensions.height);
  if (!isPostcard) return config;
  // Subtract rather than hardcode a parallel list so future changes to the
  // base print steps reach postcards too.
  return { ...config, steps: config.steps.filter(step => !POSTCARD_EXCLUDED_STEPS.includes(step)) };
};

const useConfigHelpers = (config: CardCustomizerConfig) => {
  const isStepEnabled = useCallback(
    (step: NavigationStep) => {
      return config.steps.includes(step);
    },
    [config],
  );

  return useMemo(
    () => ({
      isStepEnabled,
    }),
    [isStepEnabled],
  );
};

export const useCardCustomizerConfig = (format: StationeryDraftFormat, category: StationeryTemplateCategoryEnum, dimensions?: CardDimensions) => {
  // Depend on the primitive values so inline `{ width, height }` objects at call
  // sites don't defeat the memo.
  const width = dimensions?.width;
  const height = dimensions?.height;
  const config = useMemo(
    () => resolveCardCustomizerConfig(format, category, width === undefined || height === undefined ? undefined : { width, height }),
    [format, category, width, height],
  );
  const helpers = useConfigHelpers(config);

  return {
    config,
    helpers,
  };
};
